import { User } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useProcessing } from '../context/ProcessingContext';
import clsx from 'clsx';
import Scholar42Logo from '../assets/Scholar42.png';
import { AUTH_ENABLED } from '../config/features';

interface HeaderProps {
  hasUnsavedChanges?: boolean;
}

export function Header({ hasUnsavedChanges }: HeaderProps) {
  const { isProcessing } = useProcessing();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleLogout = () => {
    if (hasUnsavedChanges) {
      const shouldLeave = window.confirm('Are you sure you want to logout? Your changes will be lost.');
      if (!shouldLeave) {
        return;
      }
    }
    logout();
    navigate('/', { replace: true });
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowUserMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close menu when route changes
  useEffect(() => {
    return () => setShowUserMenu(false);
  }, [location.pathname]);

  const handleNavigation = (e: React.MouseEvent, path: string) => {
    if (hasUnsavedChanges) {
      e.preventDefault();
      const shouldLeave = window.confirm('Are you sure you want to leave? Your changes will be lost.');
      if (shouldLeave) {
        navigate(path);
        setShowUserMenu(false);
      }
    }
  };

  return (
    <header className="bg-[--bg-primary] border-b border-[--border-primary] fixed w-full z-10">
      <div className="w-full px-4">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link
              to="/"
              className={clsx(
                "flex items-center",
                isProcessing && "pointer-events-none opacity-50"
              )}
              onClick={(e) => {
                if (isProcessing) {
                  e.preventDefault();
                  return;
                }
                if (hasUnsavedChanges) {
                  const shouldLeave = window.confirm('Are you sure you want to leave? Your changes will be lost.');
                  if (!shouldLeave) {
                    e.preventDefault();
                  }
                }
                window.scrollTo(0, 0)
              }}
            >
              <img
                src={Scholar42Logo}
                alt="Scholar42 Logo"
                className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg object-cover"
              />
              <span className="ml-2 text-sm sm:text-base lg:text-lg font-bold text-[--text-primary] flex flex-col leading-none gap-0">
                <span className="block">
                  Scholar42
                </span>
                <span className="block text-xs font-normal text-[--text-secondary] leading-[1] mt-0">
                  by InfiniPouch
                </span>
              </span>

            </Link>
          </div>
          <div className="flex items-center space-x-4">
            <ThemeToggle disabled={isProcessing} />
            <div className="flex space-x-2">
              {user ? (
                <div className="relative">
                  <button
                    ref={buttonRef}
                    onClick={() => !isProcessing && setShowUserMenu(!showUserMenu)}
                    disabled={isProcessing}
                    className={clsx(
                      "flex items-center px-4 py-2 text-sm text-[--text-primary] hover:bg-[--state-hover] rounded-lg",
                      isProcessing && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <User className="w-5 h-5 mr-2" />
                    <span className="text-[14px]">{user.username}</span>
                  </button>

                  {showUserMenu && (
                    <div
                      ref={menuRef}
                      className="absolute right-0 mt-2 w-48 bg-[--bg-primary] rounded-md shadow-lg py-1 z-50"
                    >
                      <Link
                        to="/change-password"
                        className="block px-4 py-2 text-sm text-[--text-primary] hover:bg-[--state-hover]"
                        onClick={(e) => {
                          if (hasUnsavedChanges) {
                            handleNavigation(e, '/change-password');
                          } else {
                            setShowUserMenu(false);
                          }
                        }}
                      >
                        Change Password
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-[--text-primary] hover:bg-[--state-hover]"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : AUTH_ENABLED ? (
                <>
                  <Link
                    to="/login"
                    className="px-3 py-2 text-xs sm:text-sm font-medium text-[--text-primary] hover:bg-[--state-hover] rounded-lg"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="px-3 py-2 text-xs sm:text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg"
                  >
                    Register
                  </Link>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
